/**
 * 원격 저장소 추상화. 실제 구현 = Google Drive appDataFolder, 테스트 구현 = 메모리.
 * 동기화 엔진은 이 인터페이스만 안다 (향후 다른 저장소로 바꿔도 엔진은 그대로).
 */
export type RemoteFile = { name: string; id: string; modifiedTime: string; size?: number };

export interface RemoteStore {
  list(prefix: string): Promise<RemoteFile[]>;
  /** 없으면 null */
  read(name: string): Promise<string | null>;
  /** 같은 이름이 있으면 덮어쓰고, 없으면 만든다 */
  write(name: string, content: string): Promise<void>;
  /** 지우기 (오래된 Drive 백업 정리용). 없으면 아무것도 하지 않는다 */
  remove(name: string): Promise<void>;
}

export class RemoteUnavailableError extends Error {
  constructor(message = '원격 저장소에 연결할 수 없습니다') {
    super(message);
    this.name = 'RemoteUnavailableError';
  }
}

export class RemoteAuthError extends Error {
  constructor() {
    super('Google 연결이 만료되었습니다. 다시 연결해 주세요.');
    this.name = 'RemoteAuthError';
  }
}

/** 테스트·개발용 메모리 저장소. 여러 "기기"가 같은 인스턴스를 공유한다. */
export class MemoryRemoteStore implements RemoteStore {
  readonly files = new Map<string, { content: string; modifiedTime: string; id: string }>();
  failNext: Error | null = null;
  private clock = 0;

  private maybeFail() {
    if (this.failNext) {
      const e = this.failNext;
      this.failNext = null;
      throw e;
    }
  }

  async list(prefix: string): Promise<RemoteFile[]> {
    this.maybeFail();
    return [...this.files.entries()]
      .filter(([n]) => n.startsWith(prefix))
      .map(([name, f]) => ({ name, id: f.id, modifiedTime: f.modifiedTime, size: f.content.length }));
  }

  async read(name: string): Promise<string | null> {
    this.maybeFail();
    return this.files.get(name)?.content ?? null;
  }

  async write(name: string, content: string): Promise<void> {
    this.maybeFail();
    const prev = this.files.get(name);
    this.clock++;
    this.files.set(name, {
      content,
      id: prev?.id ?? `mem-${this.files.size + 1}`,
      modifiedTime: new Date(Date.UTC(2026, 0, 1) + this.clock * 1000).toISOString(),
    });
  }

  async remove(name: string): Promise<void> {
    this.maybeFail();
    this.files.delete(name);
  }
}
